// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import type {
  BrokerPosition,
  PerformanceParams,
  PortfolioPerformanceResp,
  OrdersSearchPayload,
} from '@shared/data-access/interfaces/api/brokerage/portfolio';
import { brokerageApi } from '@shared/data-access/api-base/citec-brokerage-api';
import type {
  Order,
  BrokerConfiguration,
  BrokerageCredentials,
  OrdersPayload,
} from '@shared/data-access/interfaces/api/brokerage/commons';
import { accountPayload } from '@shared/data-access/utils/payloads/brokerage';
import { cleanPayload } from '@shared/data-access/utils/clean-payload';
import { SharedTagTypes } from '@shared/data-access/utils/constants';

export const portfolioApi = brokerageApi.injectEndpoints({
  endpoints: build => ({
    getPositions: build.query<
      BrokerPosition[],
      { params: BrokerConfiguration; payload: BrokerageCredentials }
    >({
      query: ({ params, payload }) => ({
        url: '/v2/portfolio/positions/',
        method: 'POST',
        params: params,
        body: cleanPayload(payload, accountPayload),
      }),
      extraOptions: {
        includeCurrency: true,
      },
      providesTags: [
        { type: 'Portfolio', id: 'Positions' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    performance: build.query<
      PortfolioPerformanceResp,
      { params: PerformanceParams; payload: BrokerageCredentials }
    >({
      query: ({ params, payload }) => ({
        url: '/v2/portfolio/performance/',
        method: 'POST',
        params: params,
        body: cleanPayload(payload, accountPayload),
      }),
      extraOptions: {
        includeCurrency: true,
      },
      providesTags: [
        { type: 'Portfolio', id: 'Performance' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    postOrders: build.query<
      Order[],
      { params: BrokerConfiguration; payload: OrdersPayload }
    >({
      query: ({ params, payload }) => ({
        url: '/v2/portfolio/orders/',
        method: 'POST',
        params: params,
        body: payload,
      }),
      providesTags: [{ type: 'Portfolio', id: 'Orders' }],
    }),

    ordersSearch: build.query<
      Order[],
      { params: BrokerConfiguration; payload: OrdersSearchPayload }
    >({
      query: ({ params, payload }) => ({
        url: '/v2/portfolio/orders/search/',
        method: 'POST',
        params: params,
        body: payload,
      }),
      providesTags: [{ type: 'Portfolio', id: 'OrdersSearch' }],
    }),
  }),
});
